import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { finalize, firstValueFrom, Observable } from 'rxjs';
import {
  TransactionRequest,
  TransactionRequestPlanOffer,
} from '../../model/TransactionRequest';
import { UserService } from '../../services/user/user.service';
import {
  TransactionRequestService,
  DecisionContext,
} from '../../services/transaction-request/transaction-request.service';
import { ConfirmationDialogData } from '../../components/confirmation-dialog/confirmation-dialog.component';
import { User } from 'src/app/model/User';
import { CustomerService } from 'src/app/services/customer/customer.service';
import { FileService } from 'src/app/services/file/file.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import {
  formatPlanAmount,
  inferPlanKind,
  planDueLabel,
  planMonths,
  planPaymentLabel,
  planTermLabel,
  PlanKind,
} from 'src/app/shared/plan-pricing';

/** Data provided to the review offer dialog */
export interface ReviewOfferDialogData {
  transactionRequest: TransactionRequest;
}

/** Data returned from the review offer dialog */
export interface ReviewOfferDialogResult {
  success: boolean;
  action: 'accept' | 'decline' | 'close';
  transactionRequest: TransactionRequest | null;
}

/** View model for a selectable plan card */
export interface ReviewPlanCard {
  key: number;
  label: string;
  desc: string;
  total: number;
  totalStr: string;
  apr: number;
  aprStr: string;
  months: number;
  days: number;
  kind: PlanKind;
  monthlyStr: string;
  dueStr: string;
  termStr: string;
  hasFee: boolean;
  feeStr: string;
  fee: number;
}

/**
 * Dialog component for reviewing transaction requests (Review offer).
 * Supports multi-plan selection, TAA gating, and a 3-step accept flow.
 */
@Component({
  selector: 'app-review-offer-dialog',
  templateUrl: './review-offer-dialog.component.html',
  styleUrl: './review-offer-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ReviewOfferDialogComponent implements OnInit {
  private readonly _dialogRef: MatDialogRef<ReviewOfferDialogComponent> =
    inject(MatDialogRef);
  private readonly _transactionRequestService: TransactionRequestService =
    inject(TransactionRequestService);
  private readonly _toastrService: ToastrService = inject(ToastrService);
  private readonly _customerService: CustomerService = inject(CustomerService);
  private readonly _fileService: FileService = inject(FileService);
  public readonly _userService: UserService = inject(UserService);
  public readonly _dialogService: DialogService = inject(DialogService);

  data: ReviewOfferDialogData = inject(MAT_DIALOG_DATA);
  user$: Observable<User | null> = this._userService.user$;
  error: string | null = null;
  acceptError = '';
  files: { id: number; name: string; file: File }[] = [];

  isSubmitting = false;
  loadingCreditCheck = true;
  loadingInvoices = true;
  remainingCredit = 0;

  /** 1 = Details, 2 = Review, 3 = Confirmation */
  page: 1 | 2 | 3 = 1;
  chosenPlanKey: number | null = null;
  taaViewed: Record<number, boolean> = {};

  planCards: ReviewPlanCard[] = [];

  async ngOnInit(): Promise<void> {
    this.loadingCreditCheck = true;
    this.loadingInvoices = true;
    this.buildPlanCards();

    // Auto-select sole plan, or restore authorized plan when viewing decided request
    const plans = this.data.transactionRequest.plans;
    if (this.data.transactionRequest.authorized_plan_id != null) {
      this.chosenPlanKey = this.data.transactionRequest.authorized_plan_id;
    } else if (plans.length === 1) {
      this.chosenPlanKey = plans[0].plan_id;
    }

    this.borrowerCreditCheck();
    this.loadTransactionFiles();

    if (this.data.transactionRequest.supplier_status === 'suspended') {
      this.error =
        'Supplier is unable to accept transactions. Please contact supplier.';
    }

    const companyStatus = this._userService.company?.companyStatus;
    if (companyStatus === 'suspended') {
      this.error =
        'Your account is currently suspended. You cannot accept transaction requests at this time.';
    }
  }

  get isPending(): boolean {
    return this.data.transactionRequest.allowBorrowerAction;
  }

  get isDecided(): boolean {
    return !this.isPending;
  }

  get isMulti(): boolean {
    return this.data.transactionRequest.isMultiPlan;
  }

  get planCountLabel(): string {
    const n = this.planCards.length;
    return n === 1 ? '1 plan' : `${n} plans to choose from`;
  }

  get chosenPlan(): ReviewPlanCard | null {
    return this.planCards.find((p) => p.key === this.chosenPlanKey) ?? null;
  }

  get hasChosen(): boolean {
    return !!this.chosenPlan;
  }

  get taaViewedChosen(): boolean {
    return this.chosenPlanKey != null && !!this.taaViewed[this.chosenPlanKey];
  }

  get taaBtnLabel(): string {
    return this.taaViewedChosen
      ? 'Terms & Agreement viewed'
      : 'View Terms & Agreement';
  }

  get taaHint(): string {
    if (!this.hasChosen) {
      return 'Select a plan to continue';
    }
    if (!this.taaViewedChosen) {
      return 'View the Terms & Agreement for your selected plan before continuing';
    }
    return '';
  }

  get canReviewAccept(): boolean {
    return (
      this.isPending &&
      this.hasChosen &&
      this.taaViewedChosen &&
      !this.error &&
      !this.isSubmitting
    );
  }

  get amountDueStr(): string {
    const tr = this.data.transactionRequest;
    if (tr.status === 'accepted' && tr.authorized_total != null) {
      return formatPlanAmount(tr.authorized_total);
    }
    if (this.chosenPlan) {
      return this.chosenPlan.totalStr;
    }
    if (this.planCards.length === 1) {
      return this.planCards[0].totalStr;
    }
    if (this.planCards.length > 1) {
      const totals = this.planCards.map((p) => p.total);
      const lo = Math.min(...totals);
      const hi = Math.max(...totals);
      if (hi - lo < 0.005) {
        return formatPlanAmount(lo);
      }
      return `${formatPlanAmount(lo)} – ${formatPlanAmount(hi)}`;
    }
    return formatPlanAmount(tr.amount);
  }

  get acceptExceeds(): boolean {
    return (
      !!this.chosenPlan && this.chosenPlan.total > this.remainingCredit + 0.005
    );
  }

  get acceptExceedsMsg(): string {
    return (
      `This plan’s total of ${this.chosenPlan?.totalStr} exceeds your remaining credit of ` +
      `${formatPlanAmount(this.remainingCredit)}.`
    );
  }

  get acceptEnabled(): boolean {
    return (
      this.hasChosen &&
      this.taaViewedChosen &&
      !this.acceptExceeds &&
      !this.error &&
      !this.isSubmitting
    );
  }

  get authorizedLabel(): string {
    if (
      !this.isDecided ||
      this.data.transactionRequest.authorized_plan_id == null
    ) {
      return '';
    }
    const p = this.planCards.find(
      (c) => c.key === this.data.transactionRequest.authorized_plan_id,
    );
    return p?.label || this.data.transactionRequest.plan_desc || '';
  }

  get authorizedTotalStr(): string {
    const t = this.data.transactionRequest.authorized_total;
    if (t != null) {
      return formatPlanAmount(t);
    }
    return '';
  }

  private buildPlanCards(): void {
    const tr = this.data.transactionRequest;
    this.planCards = (tr.plans || []).map((p: TransactionRequestPlanOffer) => {
      const kind = inferPlanKind({
        name: p.name || p.description,
        duration: p.duration,
        apr: p.apr,
      });
      const months =
        kind === 'installment'
          ? planMonths({
              name: p.name || p.description,
              duration: p.duration,
              apr: p.apr,
            })
          : 0;
      const total = p.total ?? tr.amount;
      return {
        key: p.plan_id,
        label: p.name || p.description || `Plan ${p.plan_id}`,
        desc: p.description || '',
        total,
        totalStr: formatPlanAmount(total),
        apr: p.apr ?? 0,
        aprStr: p.apr ? `${p.apr.toFixed(1)}% APR` : '0% APR',
        months,
        days: p.duration,
        kind,
        monthlyStr: planPaymentLabel(kind, total, p.apr ?? 0, months),
        dueStr: planDueLabel(tr.created_at, kind, p.duration, months),
        termStr: planTermLabel(kind, p.duration, months),
        hasFee: (p.fee ?? 0) > 0.005,
        feeStr: formatPlanAmount(p.fee ?? 0),
        fee: p.fee ?? 0,
      };
    });
  }

  private async borrowerCreditCheck(): Promise<void> {
    try {
      const company = this._userService.company;
      if (!company) {
        throw new Error('No company found for user');
      }
      const companyId = company.companyId;
      if (!companyId) {
        throw new Error('No company ID found');
      }

      const summary = await firstValueFrom(
        this._customerService.getCustomerCreditSummary$(companyId),
      );
      if (!summary) {
        throw new Error('No credit summary found');
      }

      this.remainingCredit = summary.remainingCredit;

      const checkAmount =
        this.chosenPlan?.total ??
        Math.max(
          ...this.planCards.map((p) => p.total),
          this.data.transactionRequest.amount,
        );
      if (checkAmount > summary.remainingCredit) {
        // Soft warning only when every option exceeds; hard block is per chosen plan on page 2
        if (this.planCards.every((p) => p.total > summary.remainingCredit)) {
          this.error =
            'This transaction request exceeds your remaining credit limit';
        }
      }

      this.loadingCreditCheck = false;
    } catch (err) {
      console.error(
        '[REVIEW-OFFER-DIALOG]: Error loading borrower credit info:',
        err,
      );
      this._toastrService.error('Error loading borrower credit info');
      this.loadingCreditCheck = false;
    }
  }

  private async loadTransactionFiles(): Promise<void> {
    const fileIds = this.data.transactionRequest.fileIds || [];
    if (fileIds.length === 0) {
      this.loadingInvoices = false;
      return;
    }

    const downloadPromises = fileIds.map((fileId) =>
      firstValueFrom(this._fileService.downloadFile('' + fileId)),
    );

    try {
      const downloadedFiles = await Promise.all(downloadPromises);
      this.files = downloadedFiles.map((file, index) => ({
        id: fileIds[index],
        name: file.name,
        file: file,
      }));
      this.loadingInvoices = false;
    } catch (error) {
      console.error('[REVIEW-OFFER-DIALOG]: Error downloading files:', error);
      this._toastrService.error('Error downloading files');
      this.loadingInvoices = false;
    }
  }

  public choosePlan(planKey: number): void {
    if (!this.isPending) {
      return;
    }
    this.chosenPlanKey = planKey;
    this.acceptError = '';
  }

  public isPlanSelected(planKey: number): boolean {
    return this.chosenPlanKey === planKey;
  }

  public onOpenFile(index: number): void {
    const fileEntry = this.files[index];
    this._fileService.openFile(fileEntry.file);
  }

  public onTermsClick(): void {
    const transactionRequestId =
      this.data.transactionRequest.tx_request_id ?? '';
    if (!transactionRequestId) {
      this._toastrService.error(
        'Cannot open terms: missing transaction identifier',
      );
      return;
    }
    this._dialogService.openTransactionRequestOfferDialog({
      transactionRequestId,
      planId: this.chosenPlanKey ?? undefined,
    });
  }

  public openTaa(): void {
    if (!this.chosenPlanKey) {
      return;
    }
    const transactionRequestId =
      this.data.transactionRequest.tx_request_id ?? '';
    if (!transactionRequestId) {
      this._toastrService.error(
        'Cannot open terms: missing transaction identifier',
      );
      return;
    }
    const planKey = this.chosenPlanKey;
    this._dialogService
      .openTransactionRequestOfferDialog({
        transactionRequestId,
        planId: planKey,
      })
      .subscribe({
        next: () => {
          this.taaViewed = { ...this.taaViewed, [planKey]: true };
        },
        complete: () => {
          this.taaViewed = { ...this.taaViewed, [planKey]: true };
        },
      });
  }

  public goReviewPage(): void {
    if (!this.canReviewAccept) {
      return;
    }
    this.page = 2;
  }

  public backToDetails(): void {
    this.page = 1;
    this.acceptError = '';
  }

  public async onAcceptAuthorize(): Promise<void> {
    if (!this.acceptEnabled || !this.chosenPlanKey) {
      return;
    }

    this.isSubmitting = true;
    this.acceptError = '';
    const ctx = await this.buildDecisionContext();
    ctx.planId = this.chosenPlanKey;

    this._transactionRequestService
      .handleRequest(this.data.transactionRequest.id, 'accepted', ctx)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (updatedRequest: TransactionRequest) => {
          this.data.transactionRequest = updatedRequest;
          this.page = 3;
          this._toastrService.success(
            'Transaction request accepted successfully',
          );
        },
        error: (error) => {
          console.error('Error accepting transaction request:', error);
          this.acceptError =
            error?.error?.detail ||
            error?.message ||
            'Failed to accept transaction request';
          this._toastrService.error('Failed to accept transaction request');
        },
      });
  }

  public async onDeclineClick(): Promise<void> {
    if (!this.data.transactionRequest) {
      return;
    }

    const dialogData: ConfirmationDialogData = {
      title: 'Decline Transaction Request',
      message: 'Are you sure you want to decline this transaction request?',
    };

    const obs = this._dialogService.openConfirmationDialog(dialogData);
    const confirmed = await firstValueFrom(obs);
    if (!confirmed) {
      return;
    }

    this.isSubmitting = true;
    const ctx = await this.buildDecisionContext();

    this._transactionRequestService
      .handleRequest(this.data.transactionRequest.id, 'declined', ctx)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (updatedRequest: TransactionRequest) => {
          this._toastrService.success(
            'Transaction request declined successfully',
          );
          this._dialogRef.close({
            success: true,
            action: 'decline',
            transactionRequest: updatedRequest,
          });
        },
        error: (error) => {
          console.error('Error declining transaction request:', error);
          this._toastrService.error('Failed to decline transaction request');
        },
      });
  }

  public done(): void {
    this._dialogRef.close({
      success: true,
      action: 'accept',
      transactionRequest: this.data.transactionRequest,
    });
  }

  public cancel(): void {
    this._dialogRef.close({
      success: false,
      action: 'close',
      transactionRequest: null,
    });
  }

  private async buildDecisionContext(): Promise<DecisionContext> {
    const navAny = navigator as any;
    const location = await this.getBrowserLocation();

    return {
      userAgent: navigator.userAgent,
      platform: navAny.userAgentData?.platform ?? navigator.platform ?? null,
      decidedAt: new Date().toISOString(),
      location,
    };
  }

  private async getBrowserLocation(): Promise<{
    lat: number;
    lon: number;
  } | null> {
    if (!navigator.geolocation) return null;

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: false, timeout: 5000 },
      );
    });
  }
}
